import { CalendarClock } from 'lucide-react'
import { Dropdown } from '@/components/ui'
import { cn } from '@/internationalization/cn'

/**
 * Filter the action list by deadline.
 *
 * A `Dropdown` rather than a `SegmentedControl`: five fixed options is one too
 * many to sit beside Status, Owner, Patron and Meeting without wrapping, and
 * these are chosen far less often than Status is.
 *
 * "No due date" is a first-class option, not a footnote — those are the items
 * nobody has committed to a day for, which is exactly the list somebody
 * planning a week needs to work through, and they are invisible in every other
 * view because they sort last.
 *
 * "Due this week" DELIBERATELY INCLUDES overdue items and today's. It answers
 * "what do I have to get through this week", not "which items fall in a
 * window": an item that slipped from last Thursday is still this week's
 * problem, and a bucket that hid it would hide the most urgent things in it.
 * Overdue and Due today remain available as the narrower questions. The week
 * ends on Sunday (ISO 8601) and is computed server-side from the day this
 * browser reports, so it is the viewer's week — see `domain/due_dates.py`.
 */
const DUE_OPTIONS = [
  { value: 'all', label: 'Any due date' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: 'this_week', label: 'Due this week' },
  { value: 'none', label: 'No due date' },
]

export function DueFilter({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  const selected = DUE_OPTIONS.find((option) => option.value === value)

  return (
    <Dropdown
      heading="Due"
      align="start"
      trigger={({ isOpen, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-sm',
            'text-fg-muted hover:bg-surface-raised hover:text-fg',
            value !== 'all' && 'border-brand/40 bg-brand/5 text-fg',
          )}
        >
          <CalendarClock aria-hidden className="h-3.5 w-3.5" />
          {selected?.label ?? 'Any due date'}
        </button>
      )}
      items={DUE_OPTIONS.map((option) => ({
        id: option.value,
        label: option.label,
        selected: value === option.value,
        onSelect: () => onChange(option.value),
      }))}
    />
  )
}
